import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import { conductorMaterialSchema } from "../services/cableContract";
import {
  earthFaultLoopRunCreateRequestSchema,
  type EarthFaultLoopRunCreateRequest,
} from "../services/earthFaultLoop";
import {
  circuitKindSchema,
  deviceKindSchema,
  mcbTypeSchema,
  operatingCurrentBasisSchema,
  supplyKindSchema,
  systemKindSchema,
} from "../services/earthFaultLoopContract";
import type { FaultRunFaultLevel } from "../services/faultLink";
import type { SldElements } from "../services/sldElements";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import { CableRunSelector } from "./CableRunSelector";
import { FaultRunSelector, linkedDetailText as sharedLinkedDetailText } from "./FaultRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker } from "./SldElementPicker";
import {
  CABLE_SOURCE_LABELS,
  CABLE_SOURCES,
  CIRCUIT_KIND_LABELS,
  DEVICE_KIND_LABELS,
  EARTH_FAULT_LOOP_DRAFT_MODULE,
  EARTH_FAULT_LOOP_LABELS,
  EXTERNAL_IMPEDANCE_SOURCE_LABELS,
  EXTERNAL_IMPEDANCE_SOURCES,
  MCB_TYPE_LABELS,
  OPERATING_CURRENT_BASIS_LABELS,
  SUPPLY_KIND_LABELS,
  SYSTEM_KIND_LABELS,
  buildEarthFaultLoopRunPayload,
  createCircuitRowDraft,
  createInitialEarthFaultLoopDraft,
  earthFaultLoopDraftSchema,
  seedCircuitRowIdCounter,
  type CableSourceChoice,
  type CircuitRowDraft,
  type EarthFaultLoopDraft,
  type ExternalImpedanceSourceChoice,
} from "./earthFaultLoopDraft";

/** The first option of every choice: nothing is chosen for the engineer. */
const CHOOSE = "Choose…";

const CONDUCTOR_MATERIAL_LABELS = { COPPER: "Copper", ALUMINIUM: "Aluminium" } as const;

export { NO_FAULT_RUNS_NOTE, NO_OPEN_REVISION_NOTE, faultRunOptionText } from "./FaultRunSelector";
export { CABLE_RUN_HINT, NO_CABLE_RUNS_NOTE, cableRunOptionText } from "./CableRunSelector";

export const NOT_MINIMUM_EARTH_FAULT_NOTE =
  "This run is not the minimum-case single-phase-to-earth fault; the external loop impedance " +
  "is derived from it on its stated basis and the result will be Engineering review required.";

export const DECLARED_ZE_HINT =
  "A declared Ze is not traceable to a stored earth-fault study; record its source (a " +
  "measurement at the origin, the supply undertaking's value or a hand calculation). Without " +
  "a source the result is Engineering review required.";

export const NONE_ZE_HINT =
  "Without Ze the loop impedance is not formed and no circuit is compared; the conductor " +
  "figures are still printed.";

export const TEMPERATURE_HINT =
  "The conductor temperature at which R1 + R2 is evaluated is standard-defined and must be " +
  "declared with its source. A linked cable run supplies its insulation's maximum operating " +
  "temperature as the default basis; a typed value overrides it. Without a temperature the " +
  "check is not passed on the 20 °C figure.";

export const DEVICE_HINT =
  "Ia is the current that operates the device within the required time, from the " +
  "manufacturer's time-current data or a standard's table, with its source; or declare the " +
  "device's maximum Zs instead. The product holds no device curve and no tripping multiple. " +
  "Under the IN profile an MCB with a declared type B / C / D and a rating of 6 to 125 A " +
  "takes CPWD Part I 2023 App F Table F-3 as its default maximum Zs; a declared value governs.";

export const NOMINAL_UO_HINT =
  "The system's declared nominal line-to-earth voltage (230 for a 230 / 400 V system, 240 for " +
  "240 / 415 V) selects the band of CPWD App F Table F-2 for a TN final circuit under the IN " +
  "profile; it never enters the fault current, which uses Un / √3.";

export const PROFILE_NOTE =
  "The loop physics carries no profile-dependent value; the IN profile supplies the CPWD App F " +
  "tables as defaults, every other profile answers 'limit not established' until its reference " +
  "is verified. The profile is recorded with the run and must match the project revision.";

/** The read-only line under the selector: the run's figures without the peak. */
export function linkedDetailText(level: FaultRunFaultLevel): string {
  return sharedLinkedDetailText(level, false);
}

/** The run the loop wants: the minimum-case single-phase-to-earth fault (D3). */
export function isMinimumEarthFault(level: FaultRunFaultLevel): boolean {
  return level.calculation_case === "MINIMUM" && level.fault_type === "SINGLE_PHASE_TO_EARTH";
}

type EarthFaultLoopFormProps = {
  disabled?: boolean;
  onSubmit: (payload: EarthFaultLoopRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): the board's bus
  // and each row's feeder are picked from it (D17).
  sldElements?: SldElements | null | undefined;
};

// The earth-fault loop study of one board (EOS-05d). The form owns the draft; Ze starts on a
// linked earth-fault run of the active open revision (D3), each row's cable on a linked cable
// run (D5); the figures are read by the backend when the study is saved. Every choice starts
// unchosen: the backend imposes no default. The draft is kept under its own module key.
export function EarthFaultLoopForm({ disabled = false, onSubmit, sldElements }: EarthFaultLoopFormProps) {
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<EarthFaultLoopDraft>({
      module: EARTH_FAULT_LOOP_DRAFT_MODULE,
      createInitial: createInitialEarthFaultLoopDraft,
      schema: earthFaultLoopDraftSchema,
      revisionId,
    });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [rowToFocus, setRowToFocus] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (rowToFocus === null) {
      return;
    }
    formRef.current?.querySelector<HTMLInputElement>(`input[name="${rowToFocus}-code"]`)?.focus();
    setRowToFocus(null);
  }, [rowToFocus]);

  function updateStudy(patch: Partial<EarthFaultLoopDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  const setFaultRunId = useCallback(
    (faultRunId: string) => setDraft((current) => ({ ...current, faultRunId })),
    [setDraft],
  );

  function updateRow(id: string, patch: Partial<CircuitRowDraft>) {
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  }

  // Stable per row for the cable selector's effect that clears an unlisted run.
  const setCableRunId = useCallback(
    (id: string, cableRunId: string) =>
      setDraft((current) => ({
        ...current,
        rows: current.rows.map((row) => (row.id === id ? { ...row, cableRunId } : row)),
      })),
    [setDraft],
  );

  function addRow() {
    seedCircuitRowIdCounter(draft.rows.map((row) => row.id));
    const row = createCircuitRowDraft();
    setDraft((current) => ({ ...current, rows: [...current.rows, row] }));
    setRowToFocus(row.id);
  }

  function removeRow(id: string) {
    setDraft((current) => ({ ...current, rows: current.rows.filter((row) => row.id !== id) }));
  }

  function clearForm() {
    setValidationError(null);
    clear();
  }

  // The selectors' own sentences stand alone; every other issue is read by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    const last = issue.path.at(-1);
    if (issue.code === "custom" && (last === "fault_run_id" || last === "cable_run_id")) {
      return issue.message;
    }
    return describeValidationIssue(issue, EARTH_FAULT_LOOP_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = earthFaultLoopRunCreateRequestSchema.safeParse(
      buildEarthFaultLoopRunPayload(draft),
    );

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? describeIssue(issue) : "Review the earth-fault loop inputs.");
      return;
    }

    await onSubmit(parsed.data);
  }

  const studyText = (
    label: string,
    field: keyof EarthFaultLoopDraft,
    name: string,
    inputMode: "decimal" | "text" = "text",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={name}
        value={draft[field] as string}
        onChange={(event) =>
          updateStudy({ [field]: event.target.value } as Partial<EarthFaultLoopDraft>)
        }
      />
    </label>
  );

  const studyChoice = <T extends string>(
    label: string,
    field: keyof EarthFaultLoopDraft,
    name: string,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={name}
        value={draft[field] as string}
        onChange={(event) =>
          updateStudy({ [field]: event.target.value } as Partial<EarthFaultLoopDraft>)
        }
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  // Field names carry the row's id ("circuit-3-code"), stable across add and remove.
  const rowText = (
    row: CircuitRowDraft,
    label: string,
    field: keyof CircuitRowDraft,
    inputMode: "decimal" | "text" = "decimal",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={`${row.id}-${field}`}
        value={row[field]}
        onChange={(event) => updateRow(row.id, { [field]: event.target.value })}
      />
    </label>
  );

  const rowChoice = <T extends string>(
    row: CircuitRowDraft,
    label: string,
    field: keyof CircuitRowDraft,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={`${row.id}-${field}`}
        value={row[field]}
        onChange={(event) => updateRow(row.id, { [field]: event.target.value })}
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  const busCode = draft.busCode.trim();

  return (
    <form aria-label="Earth-fault loop inputs" ref={formRef} onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice onKeep={keepForThisRevision} onClear={clearForm} disabled={disabled} />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>
        {studyText("Study code", "code", "studyCode")}
        {studyText("Study name", "name", "studyName")}
        <SldElementPicker
          label="Bus code"
          name="busCode"
          value={draft.busCode}
          onChange={(value) => updateStudy({ busCode: value })}
          categories={["bus"]}
          elements={sldElements}
        />
        {studyChoice("System earthing", "systemKind", "systemKind", systemKindSchema.options, SYSTEM_KIND_LABELS)}
        {studyChoice("Supply", "supplyKind", "supplyKind", supplyKindSchema.options, SUPPLY_KIND_LABELS)}
        {studyText("System nominal voltage Un (V)", "systemNominalVoltageV", "systemNominalVoltageV", "decimal")}
        {studyText("Frequency (Hz)", "frequencyHz", "frequencyHz", "decimal")}
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">Australia / New Zealand (AS/NZS 3000) - reference data pending</option>
          </select>
        </label>
        <p data-profile-note="true">{PROFILE_NOTE}</p>
        {studyText("Study notes", "notes", "studyNotes")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>External loop impedance Ze</legend>
        <label>
          Ze from
          <select
            name="externalImpedanceSource"
            value={draft.externalImpedanceSource}
            onChange={(event) =>
              updateStudy({ externalImpedanceSource: event.target.value as ExternalImpedanceSourceChoice })
            }
          >
            {EXTERNAL_IMPEDANCE_SOURCES.map((source) => (
              <option key={source} value={source}>
                {EXTERNAL_IMPEDANCE_SOURCE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>
        {draft.externalImpedanceSource === "LINKED" ? (
          <FaultRunSelector
            revisionId={revisionId}
            faultRunId={draft.faultRunId}
            onChange={setFaultRunId}
            busCode={busCode}
            withPeak={false}
            notMaximumNote={NOT_MINIMUM_EARTH_FAULT_NOTE}
            isExpectedRun={isMinimumEarthFault}
          />
        ) : null}
        {draft.externalImpedanceSource === "DECLARED" ? (
          <>
            {studyText("Declared Ze (Ω)", "declaredExternalImpedanceOhm", "declaredExternalImpedanceOhm", "decimal")}
            {studyText("Ze source", "declaredExternalImpedanceSource", "declaredExternalImpedanceSource")}
            <p data-declared-ze-hint="true">{DECLARED_ZE_HINT}</p>
          </>
        ) : null}
        {draft.externalImpedanceSource === "NONE" ? <p data-none-ze-hint="true">{NONE_ZE_HINT}</p> : null}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Voltage basis</legend>
        {studyText("Minimum voltage factor C_min", "minimumVoltageFactor", "minimumVoltageFactor", "decimal")}
        {studyText("Voltage factor source", "voltageFactorSource", "voltageFactorSource")}
        {studyText("Declared nominal Uo (V)", "nominalLineToEarthVoltageV", "nominalLineToEarthVoltageV", "decimal")}
        {studyText("Nominal Uo source", "nominalVoltageSource", "nominalVoltageSource")}
        <p data-nominal-uo-hint="true">{NOMINAL_UO_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Circuits</legend>
        {draft.rows.map((row, index) => {
          const number = index + 1;
          const linkedCable = row.cableSource === "LINKED";
          const isMcb = row.deviceKind === "MCB";
          return (
            <fieldset key={row.id} data-circuit-row={row.id}>
              <legend>Circuit {number}</legend>

              <fieldset>
                <legend>Circuit</legend>
                {rowText(row, "Circuit code", "code", "text")}
                {rowText(row, "Circuit name", "name", "text")}
                {rowChoice(row, "Circuit kind", "circuitKind", circuitKindSchema.options, CIRCUIT_KIND_LABELS)}
                <SldElementPicker
                  label="Feeder code"
                  name={`${row.id}-sldElementCode`}
                  value={row.sldElementCode}
                  onChange={(value) => updateRow(row.id, { sldElementCode: value })}
                  categories={["feeder"]}
                  elements={sldElements}
                />
              </fieldset>

              <fieldset>
                <legend>Cable</legend>
                <label>
                  Cable source
                  <select
                    name={`${row.id}-cableSource`}
                    value={row.cableSource}
                    onChange={(event) =>
                      updateRow(row.id, { cableSource: event.target.value as CableSourceChoice })
                    }
                  >
                    {CABLE_SOURCES.map((source) => (
                      <option key={source} value={source}>
                        {CABLE_SOURCE_LABELS[source]}
                      </option>
                    ))}
                  </select>
                </label>
                {linkedCable ? (
                  revisionId === null ? null : (
                    <CableRunSelector
                      revisionId={revisionId}
                      name={`${row.id}-cableRunId`}
                      cableRunId={row.cableRunId}
                      onChange={(cableRunId) => setCableRunId(row.id, cableRunId)}
                    />
                  )
                ) : (
                  <>
                    {rowText(row, "Line conductor area S1 (mm²)", "lineConductorAreaMm2")}
                    {rowText(row, "Protective conductor area S2 (mm²)", "protectiveConductorAreaMm2")}
                    {rowChoice(
                      row,
                      "Conductor material",
                      "conductorMaterial",
                      conductorMaterialSchema.options,
                      CONDUCTOR_MATERIAL_LABELS,
                    )}
                    {rowText(row, "Route length (m)", "routeLengthM")}
                    {rowText(row, "Parallel runs", "parallelRuns")}
                    {rowText(row, "Protective conductor runs", "protectiveConductorRuns")}
                    {rowText(row, "Cable figures source", "cableSourceText", "text")}
                  </>
                )}
                {rowText(row, "Declared line resistance r20 (Ω/km)", "lineResistanceOhmPerKm20c")}
                {rowText(
                  row,
                  "Declared protective conductor resistance r20 (Ω/km)",
                  "protectiveResistanceOhmPerKm20c",
                )}
                {rowText(row, "Resistance source", "resistanceSource", "text")}
                {rowText(row, "Reactance per conductor (Ω/km)", "reactanceOhmPerKm")}
                {rowText(row, "Reactance source", "reactanceSource", "text")}
                {rowText(row, "Conductor temperature θ (°C)", "conductorTemperatureC")}
                {rowText(row, "Temperature source", "temperatureSource", "text")}
                <p data-temperature-hint="true">{TEMPERATURE_HINT}</p>
              </fieldset>

              <fieldset>
                <legend>Protective device</legend>
                {rowText(row, "Device code", "deviceCode", "text")}
                {rowChoice(row, "Device kind", "deviceKind", deviceKindSchema.options, DEVICE_KIND_LABELS)}
                {isMcb
                  ? rowChoice(row, "MCB type", "mcbType", mcbTypeSchema.options.filter((type) => type !== "NOT_APPLICABLE"), MCB_TYPE_LABELS)
                  : null}
                {rowText(row, "Rated current In (A)", "ratedCurrentA")}
                {rowText(row, "Rated voltage (V)", "ratedVoltageV")}
                {rowText(row, "Characteristic (as the data sheet names it)", "characteristicText", "text")}
                {rowText(row, "Required disconnection time (s)", "requiredDisconnectionTimeS")}
                {rowText(row, "Disconnection time source", "timeSource", "text")}
                {rowText(row, "Operating current Ia (A)", "operatingCurrentA")}
                {rowText(row, "Operating current source", "operatingCurrentSource", "text")}
                {rowChoice(
                  row,
                  "Operating current basis",
                  "operatingCurrentBasis",
                  operatingCurrentBasisSchema.options,
                  OPERATING_CURRENT_BASIS_LABELS,
                )}
                {rowText(row, "Declared maximum Zs (Ω)", "maximumLoopImpedanceOhm")}
                {rowText(row, "Maximum Zs source", "maximumLoopImpedanceSource", "text")}
                {rowText(row, "Minimum margin (%)", "minimumMarginPercent")}
                <p data-device-hint="true">{DEVICE_HINT}</p>
              </fieldset>

              {draft.rows.length > 1 ? (
                <button type="button" onClick={() => removeRow(row.id)}>
                  Remove circuit {number}
                </button>
              ) : null}
            </fieldset>
          );
        })}
        <button type="button" onClick={addRow}>
          Add circuit
        </button>
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Check earth-fault loop
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default EarthFaultLoopForm;
